/* ==========================================================================
   360DT COURSE FINDER v3 (1 Oct 2026), on top of v2 (10 Sep 2026)

   v3: the quiz adapts. Six questions; the third is a follow-up that changes
   with the goal (what you build first / which part of the FDE job / which
   Claude exam / which cloud / what sounds like you). The v1 rule tree is gone:
   a fixed points model (GOAL, FOCUS, level, coding, priority, PROMO, TIE)
   ranks all programs after every answer, so results are deterministic and
   every answer moves the live panel. The goal options promote AI Engineer,
   FDE, Claude and Cloud/DevOps. The quiz string sent with the lead gains
   focus=..., which Code.gs quizAnswers_() passes through. Rules are checked
   over every answer combination by tools/check-rules.js.

   Loaded (deferred) by snippets/dt-course-finder.liquid, styled by
   assets/dt-finder.css. Replaces the v1 finder that lived inside
   snippets/dt-lead-engine.liquid, and keeps its contract: the same five
   questions, the same recommend() rules, the same Apps Script OTP backend with
   tag "quiz-finder", the same placement names and the same dtle_* events, so
   leads and reports land exactly as before.

   What changed for the visitor:
   - The "Find my course" launcher now sits directly above the Tidio chat
     bubble, measured from Tidio's own shadow DOM, and hides while the chat
     window is open. v1 ignored Tidio (its host is a full-screen layer), so the
     two overlapped on desktop.
   - A live panel ranks the top 3 programs after every answer, with animated
     match bars. Names stay blurred until the result, so the contact step still
     has something to unlock.
   - Six-box OTP entry with paste and auto-submit, a resend countdown, and an
     animated result reveal with duration, live hours, schedule and fee.
   - Bottom sheet on phones (drag the dark header down to close), keyboard
     shortcuts 1-5 on desktop, focus trap, reduced-motion support.

   Depends on window.dtLECore from dt-lead-engine for attribution parameters,
   the backend URL, lead bookkeeping and events. Without it the finder still
   opens and scores, but cannot send a code.
   ========================================================================== */
(() => {
  'use strict';
  if (window.dtLE && window.dtLE.version) return;

  const D = document;
  const HTML = D.documentElement;
  const DAY = 864e5;
  let reduce = false;
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  /* ------------------------------------------------------------------ core */
  const core = window.dtLECore || (() => {
    const get = k => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return null; } };
    const set = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} };
    const root = D.getElementById('dtle');
    let mobile = false;
    try { mobile = window.matchMedia('(max-width: 768px), (pointer: coarse)').matches; } catch (e) {}
    return {
      GAS: '', PAGE: root ? root.getAttribute('data-page') || 'other' : 'other', isMobile: mobile,
      params: () => ({}),
      qs: o => Object.keys(o).filter(k => o[k] !== undefined && o[k] !== null && o[k] !== '').map(k => encodeURIComponent(k) + '=' + encodeURIComponent(o[k])).join('&'),
      track: (name, data) => { try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data || {})); } catch (e) {} },
      onLead: () => {}, isLead: () => false, lsGet: get, lsSet: set
    };
  })();
  const PAGE = core.PAGE;
  const TAG = 'quiz-finder';
  const WA = '917091464529';
  const CDN = 'https://cdn.shopify.com/s/files/1/0880/6141/1605/files/';
  const LOGO = {
    ms: [CDN + 'microsoft-logo.webp?v=1783096726&width=64', 'Microsoft'],
    cl: [CDN + 'starburst_icon_3b7d546e-b2d9-4daf-b907-5144013e6bb7.png?v=1783160268&width=64', 'Claude'],
    aws: [CDN + 'aws-logo-transparent.webp?v=1783098060&width=96', 'AWS']
  };
  const DATA = (() => { try { return JSON.parse(D.getElementById('dtf-data').textContent); } catch (e) { return {}; } })();
  /* The store money format prints "Rs. 29,999"; the course pages show ₹29,999, so match them. */
  const priceOf = h => String((DATA.prices && DATA.prices[h]) || (window.DTP_PRICES && window.DTP_PRICES[h]) || '').replace(/^Rs\.?\s*/, '₹');

  /* --------------------------------------------------------------- courses
     n name, u landing page, g group line, l logos, h product handle, w why,
     wk weeks, hrs live hours (the header and carousel publish the same
     figures), and the scoring profile: tr tracks, lv level 0-3, cd coding
     needed 0-3, sal / cert / proj strengths 0-3, fee tier 0-3 (0 cheapest). */
  const C = {
    'FDE': { n: 'Forward Deployed Engineer (FDE)', u: '/pages/forward-deployed-engineer-course', g: 'AI engineering · Microsoft + Claude certified', l: 'ms,cl', h: 'forward-deployed-engineer-course', wk: 18, hrs: '110+',
      w: 'You deploy AI systems inside real enterprises: the role companies are hiring for right now, and the highest-paid track we run.', tr: ['fde', 'ai'], lv: 2.5, cd: 3, sal: 3, cert: 2, proj: 3, fee: 3 },
    'AIENG': { n: 'AI Engineer: Generative AI, RAG and AI Agents', u: '/pages/ai-engineer-course', g: 'AI engineering · Microsoft + Claude certified', l: 'ms,cl', h: 'ai-engineer-course', wk: 16, hrs: '100+', /* cd 2.5: starts from Python basics; FDE assumes daily coding */
      w: 'You build with LLMs, retrieval and agents from week one, and finish with both a Microsoft and a Claude certification.', tr: ['ai', 'fde'], lv: 1.5, cd: 2.5, sal: 3, cert: 3, proj: 2, fee: 3 },
    'AI-103': { n: 'Generative AI Developer (Azure AI-103 + Claude CCAR-F)', u: '/pages/generative-ai-developer-course', g: 'Microsoft Azure + Claude', l: 'ms,cl', h: 'generative-ai-developer-course', wk: 8, hrs: '50+',
      w: 'The gentlest way into AI engineering if your code is still warming up: Azure does the heavy lifting, you learn the patterns.', tr: ['ai', 'fde'], lv: 1, cd: 2, sal: 2, cert: 3, proj: 2, fee: 2 },
    'AI-200': { n: 'Azure AI Cloud Developer (AI-200 + Claude CCAR-F)', u: '/pages/azure-ai-developer-course', g: 'Microsoft Azure + Claude', l: 'ms,cl', h: 'azure-ai-developer-course', wk: 8, hrs: '50+',
      w: 'Cloud-native AI apps on Azure, with the Claude architect foundations on top.', tr: ['ai', 'cloud', 'fde'], lv: 1.5, cd: 2, sal: 2, cert: 3, proj: 2, fee: 2 },
    'DP-700': { n: 'Microsoft Fabric Data Engineer (DP-700 + DP-900 + Claude)', u: '/pages/data-engineer-course-microsoft-fabric', g: 'Microsoft Azure + Claude', l: 'ms,cl', h: 'data-engineer-course-microsoft-fabric', wk: 8, hrs: '50+',
      w: 'Pipelines, lakehouses and Fabric, the platform Microsoft is moving every data team onto. Two certifications, one course.', tr: ['data'], lv: 1.5, cd: 2, sal: 2, cert: 3, proj: 2, fee: 2 },
    'PL-300': { n: 'Data Analyst: Excel, SQL, Python and Power BI (PL-300)', u: '/pages/data-analyst-course', g: 'Microsoft certified', l: 'ms', h: 'data-analyst-course', wk: 10, hrs: '70+',
      w: 'The most hired-for entry point into data. You need no code to start and you leave with Power BI, SQL and Python.', tr: ['data'], lv: 0.5, cd: 0.5, sal: 1, cert: 2, proj: 3, fee: 1 },
    'AWS-DOP': { n: 'AWS Solutions Architect and DevOps Engineer (SAA-C03 + DOP-C02)', u: '/pages/aws-solutions-architect-devops-course', g: 'AWS certified', l: 'aws', h: 'aws-solutions-architect-devops-course', wk: 12, hrs: '80+',
      w: 'Two of the most in-demand cloud certifications, taught together the way real teams use them.', tr: ['cloud'], lv: 1.5, cd: 1.5, sal: 2, cert: 3, proj: 2, fee: 2 },
    'AZ-DOP': { n: 'Azure Solutions Architect and DevOps Engineer (AZ-305 + AZ-400)', u: '/pages/azure-solutions-architect-devops-course', g: 'Microsoft certified', l: 'ms', h: 'azure-solutions-architect-devops-course', wk: 12, hrs: '80+',
      w: 'Architecture plus delivery on the cloud most Indian enterprises already run on.', tr: ['cloud'], lv: 2.5, cd: 1.5, sal: 2, cert: 3, proj: 2, fee: 2 },
    'AB-900': { n: 'Microsoft 365 Copilot and Agent Administrator (AB-900)', u: '/pages/microsoft-copilot-administrator-course', g: 'Microsoft certified', l: 'ms', h: 'microsoft-copilot-administrator-course', wk: 4, hrs: '30+',
      w: 'Bring AI into the tools your company already uses, no programming required, and be the person who runs it.', tr: ['cloud', 'ai'], lv: 1, cd: 0, sal: 1, cert: 2, proj: 1, fee: 0 },
    'CCAO-F': { n: 'Claude Certified Associate Foundations (CCAO-F)', u: '/pages/claude-certified-associate-prep-course', g: 'Claude certification prep', l: 'cl', h: 'claude-certified-associate-course', wk: 4, hrs: '30+',
      w: 'The foundations certification: how Claude thinks, how to work with it well, no code needed. The quickest credential on this list.', tr: ['claude'], lv: 0.5, cd: 0, sal: 1, cert: 3, proj: 1, fee: 0 },
    'CCDV-F': { n: 'Claude Certified Developer Foundations (CCDV-F)', u: '/pages/claude-certified-developer-prep-course', g: 'Claude certification prep', l: 'cl', h: 'claude-certified-developer-course', wk: 6, hrs: '40+',
      w: 'For developers who want to build on Claude and prove it: the API, tools, agents and the exam that certifies them.', tr: ['claude'], lv: 1, cd: 3, sal: 2, cert: 3, proj: 2, fee: 1 },
    'CCAR-F': { n: 'Claude Certified Architect Foundations (CCAR-F)', u: '/pages/claude-certified-architect-foundations-prep-course', g: 'Claude certification prep', l: 'cl', h: 'claude-certified-architect-foundations-course', wk: 6, hrs: '40+',
      w: 'Designing Claude-based systems: retrieval, agents, evaluation and safety, at the level a team lead needs.', tr: ['claude'], lv: 2, cd: 2, sal: 2, cert: 3, proj: 1, fee: 1 },
    'CCAR-P': { n: 'Claude Certified Architect Professional (CCAR-P)', u: '/pages/claude-certified-architect-professional-prep-course', g: 'Claude certification prep', l: 'cl', h: 'claude-certified-architect-professional-course', wk: 8, hrs: '50+',
      w: 'The senior credential: enterprise-scale Claude architecture for people who already own systems.', tr: ['claude'], lv: 3, cd: 2.5, sal: 3, cert: 3, proj: 1, fee: 2 }
  };
  const KEYS = Object.keys(C);

  /* ------------------------------------------------------------ the model
     v3 (1 Oct 2026). No rule tree: every answer adds fixed points to every
     course from the tables below, the highest total wins, and ties break by
     TIE order. Same answers, same ranking, every time; and every answer moves
     the live ranking, which is what makes the panel feel alive.

       goal      GOAL[goal][course], or OFF_GOAL (-30) for a course outside it
       focus     the follow-up question, which changes with the goal (FOCUS)
       role      level fit: 16 - 7 x |your level - course level|
       code      coding fit: 18, minus 11 per level the course needs above
                 yours (minus 3 per level below), and READY_GAP (24) more for every
                 level of gap beyond the first, so a big gap costs more and more
       priority  3 x the course's strength on what you said matters most
       when      +2 for programmes of 8 weeks or less if you start this month
       PROMO     a small fixed nudge toward the tracks 360DT is promoting:
                 AI Engineer, FDE, the Claude exams, AWS and Azure

     The goal's core courses (GOAL weight 24 or more) always rank first; the
     points order them. Two guarantees on top: the course you named (AI Engineer, FDE, or an exam
     or platform you picked in the follow-up) is never lower than second, and
     when it is second the result says why (see whyNot). */
  const LV = { student: 0, early: 1, mid: 2, senior: 3 };
  const CD = { daily: 3, some: 2, nocode: 1, none: 0 };
  const OFF_GOAL = -30, READY_GAP = 24, CORE = 24;
  const GOAL = {
    ai: { 'AIENG': 40, 'FDE': 26, 'AI-103': 34, 'AI-200': 18, 'CCDV-F': 10, 'CCAR-F': 8 },
    fde: { 'FDE': 40, 'AIENG': 38, 'AI-200': 14, 'AI-103': 12, 'CCAR-F': 10, 'CCAR-P': 8 },
    claude: { 'CCAO-F': 30, 'CCDV-F': 30, 'CCAR-F': 30, 'CCAR-P': 30, 'AIENG': 10, 'FDE': 8 },
    cloud: { 'AWS-DOP': 34, 'AZ-DOP': 34, 'AB-900': 24, 'AI-200': 18 },
    unsure: { 'AIENG': 16, 'FDE': 14, 'CCAO-F': 12, 'AI-103': 12, 'AWS-DOP': 10, 'AB-900': 10, 'CCDV-F': 10, 'AZ-DOP': 8, 'CCAR-F': 8, 'PL-300': 4, 'DP-700': 4 }
  };
  const PROMO = { 'FDE': 6, 'AIENG': 6, 'CCAO-F': 4, 'CCDV-F': 4, 'CCAR-F': 4, 'CCAR-P': 4, 'AWS-DOP': 3, 'AZ-DOP': 3, 'AI-103': 1, 'AI-200': 1, 'AB-900': 1 };
  const TIE = ['FDE', 'AIENG', 'CCAR-F', 'CCDV-F', 'CCAO-F', 'CCAR-P', 'AWS-DOP', 'AZ-DOP', 'AI-103', 'AI-200', 'AB-900', 'DP-700', 'PL-300'];
  const NAMED = { ai: 'AIENG', fde: 'FDE' };

  /* The follow-up question. o: [value, icon, label, sub, weights, why-line, live-panel note, picked course] */
  const FOCUS = {
    ai: { q: 'What do you want to build first?', s: 'This decides where your first projects start.', o: [
      ['rag', '📚', 'Assistants that answer from company data', 'RAG, search and citations', { 'AIENG': 14, 'AI-103': 10, 'AI-200': 6 },
        'you want assistants grounded in real company data, which this track builds hands-on', 'Retrieval-first builders: AI Engineer and the Azure AI tracks move up.'],
      ['agents', '🤖', 'AI agents that take actions', 'Tools, workflows, multi-step tasks', { 'AIENG': 14, 'FDE': 8, 'CCDV-F': 4 },
        'you want agents that act, not just chat, and agents are core to this track', 'Agent builders: AI Engineer leads, FDE close behind.'],
      ['azure', '🔷', 'AI apps on Microsoft Azure', 'Azure AI services, cloud-native', { 'AI-103': 14, 'AI-200': 14, 'AIENG': 4 },
        'you want to build on Azure, the cloud most Indian enterprises already run on', 'Azure builders: the Microsoft AI tracks move up.'],
      ['ship', '🚀', 'Ship AI inside real companies', 'Client-facing, end to end', { 'FDE': 20, 'AIENG': 6 },
        'you want to take AI into real companies end to end, which is the Forward Deployed Engineer job', 'Shipping AI at companies is the FDE job, so FDE jumps up.']] },
    fde: { q: 'Which part of the FDE job pulls you in most?', s: 'Forward Deployed Engineers do all of these. Your answer sets where you start.', o: [
      ['client', '🤝', 'Working with customers', 'Scoping problems, designing the fix', { 'FDE': 14, 'CCAR-F': 6 },
        'you like the customer side of the role: discovery, scoping and solution design', 'Customer-facing work is the heart of the FDE role.'],
      ['build', '🛠️', 'Building agents and integrations', 'RAG, agents and MCP tools', { 'FDE': 12, 'AIENG': 10, 'CCDV-F': 4 },
        'you want to build the agents and integrations that make up most of the job', 'Builders: FDE and AI Engineer both go deep on agents.'],
      ['prod', '☁️', 'Taking AI to production', 'Cloud, monitoring, reliability', { 'FDE': 12, 'AI-200': 8, 'AZ-DOP': 4 },
        'you care about running AI in production, not just in demos', 'Production focus: FDE leads, cloud AI tracks move up.'],
      ['all', '🎯', 'All of it', 'The full role, end to end', { 'FDE': 16 },
        'you want the whole role, end to end', 'The full role: FDE takes the lead.']] },
    claude: { q: 'Which Claude certification are you aiming for?', s: 'Not sure? Pick "Help me choose" and your level and coding decide.', o: [
      ['ccao', '💬', 'Associate', 'Use Claude well at work, no code', { 'CCAO-F': 24 },
        'you are aiming for the Associate exam, the quickest Claude credential', 'Associate locked in as your target.', 'CCAO-F'],
      ['ccdv', '💻', 'Developer', 'Build on the Claude API', { 'CCDV-F': 24 },
        'you are aiming for the Developer exam: the API, tools and agents', 'Developer locked in as your target.', 'CCDV-F'],
      ['ccar', '🏗️', 'Architect Foundations', 'Design Claude-based systems', { 'CCAR-F': 24 },
        'you are aiming for Architect Foundations: retrieval, agents, evaluation and safety', 'Architect Foundations locked in as your target.', 'CCAR-F'],
      ['ccarp', '🏛️', 'Architect Professional', 'Enterprise-scale architecture', { 'CCAR-P': 24 },
        'you are aiming for Architect Professional, the senior Claude credential', 'Architect Professional locked in as your target.', 'CCAR-P'],
      ['pick', '🧭', 'Help me choose', 'Match me by level and coding', {},
        '', 'Your level and coding answers will pick the exam.']] },
    cloud: { q: 'Which platform do you want to work on?', s: 'Pick the one your company, or the companies you want to join, run on.', o: [
      ['aws', '🟧', 'AWS', 'Architect and DevOps on AWS', { 'AWS-DOP': 22 },
        'you want AWS, and this pairs Solutions Architect with DevOps Engineer', 'AWS locked in.', 'AWS-DOP'],
      ['azure', '🔷', 'Microsoft Azure', 'Architect and DevOps on Azure', { 'AZ-DOP': 22, 'AI-200': 6 },
        'you want Azure, the cloud most Indian enterprises already run on', 'Azure locked in.', 'AZ-DOP'],
      ['m365', '🧑‍💼', 'Microsoft 365 and Copilot', 'Run Copilot and agents, no code', { 'AB-900': 24 },
        'you want to run Copilot and agents across Microsoft 365, no programming needed', 'Microsoft 365 and Copilot locked in.', 'AB-900'],
      ['pick', '🧭', 'Help me choose', 'Match me by level and coding', {},
        '', 'Your level and coding answers will pick the platform.']] },
    unsure: { q: 'Which sounds most like you?', s: 'Go with your gut. There is no wrong answer.', o: [
      ['builder', '🧱', 'I like building things', 'Code, apps, automation', { 'AIENG': 12, 'FDE': 8, 'CCDV-F': 6 },
        'you like building, and this is the most hands-on track for your level', 'Builders lean to AI Engineer and FDE.'],
      ['solver', '🧩', 'I like solving business problems', 'People, processes, outcomes', { 'FDE': 12, 'CCAR-F': 6, 'AB-900': 4 },
        'you like solving business problems, which is where this role spends its time', 'Problem solvers lean to FDE and Claude architecture.'],
      ['systems', '🖥️', 'I like running systems', 'Cloud, infrastructure, uptime', { 'AWS-DOP': 12, 'AZ-DOP': 10, 'AB-900': 6 },
        'you like running systems, and this is the infrastructure side of the stack', 'Systems people lean to AWS and Azure.'],
      ['quick', '⚡', 'I want a quick credential', 'Certified in weeks, not months', { 'CCAO-F': 12, 'CCDV-F': 12, 'CCAR-F': 10, 'AB-900': 10, 'AIENG': -8, 'FDE': -10 },
        'you want a recognised credential fast, and this is one of the shortest on the list', 'Quick credentials: Claude Associate and Copilot move up.']] }
  };
  const focusOpt = a => { const f = a.goal && a.focus && FOCUS[a.goal]; return f ? f.o.find(o => o[0] === a.focus) || null : null; };
  const pickOf = a => { const o = focusOpt(a); return (o && o[7]) || NAMED[a.goal] || ''; };
  const SHORT = { 'FDE': 'FDE', 'AIENG': 'AI Engineer', 'AI-103': 'Generative AI Developer', 'AI-200': 'Azure AI Cloud Developer', 'DP-700': 'Fabric Data Engineer',
    'PL-300': 'Data Analyst', 'AWS-DOP': 'AWS Architect and DevOps', 'AZ-DOP': 'Azure Architect and DevOps', 'AB-900': 'Copilot Administrator',
    'CCAO-F': 'Claude Associate', 'CCDV-F': 'Claude Developer', 'CCAR-F': 'Claude Architect Foundations', 'CCAR-P': 'Claude Architect Professional' };
  const short = k => SHORT[k] || C[k].n.split(/ \(|: /)[0];

  const scoreOf = (k, a) => {
    const c = C[k];
    let s = 50 + (PROMO[k] || 0);
    if (a.goal) s += GOAL[a.goal] && GOAL[a.goal][k] !== undefined ? GOAL[a.goal][k] : OFF_GOAL;
    const f = focusOpt(a);
    if (f) s += f[4][k] || 0;
    if (a.role) s += 16 - 7 * Math.abs(LV[a.role] - c.lv);
    if (a.code) { const d = c.cd - CD[a.code]; s += 18 - (d > 0 ? 11 * d + READY_GAP * Math.max(0, d - 1) : -3 * d); }
    if (a.priority) s += 3 * ({ salary: c.sal, cert: c.cert, projects: c.proj, fee: 3 - c.fee })[a.priority];
    if (a.when === 'now' && c.wk <= 8) s += 2;
    return s;
  };

  const ANSWER_KEYS = ['role', 'goal', 'focus', 'code', 'when', 'priority'];
  const NQ = ANSWER_KEYS.length;
  const scoreAll = a => {
    /* Core courses of the chosen goal (GOAL weight CORE or more) always rank
       above the rest, so a big coding gap reorders the goal's own courses and
       never pushes the visitor out of the goal they asked for. */
    const g = GOAL[a.goal] || {};
    const core = k => (g[k] || 0) >= CORE ? 1 : 0;
    const out = KEYS.map(k => ({ k, s: scoreOf(k, a), t: core(k) }));
    out.sort((x, y) => (y.t - x.t) || (y.s - x.s) || (TIE.indexOf(x.k) - TIE.indexOf(y.k)));
    const named = pickOf(a), j = named ? out.findIndex(o => o.k === named) : -1;
    if (j > 1) { const p = out.splice(j, 1)[0]; p.s = Math.max(p.s, out[1].s + 0.5); out.splice(1, 0, p); }
    /* Percent falls away from the leader by score gap and by rank, so the top
       three read as a real ordering rather than 97 / 96 / 95. The ceiling
       rises with each answer: the more we know, the stronger the best fit. */
    const answered = ANSWER_KEYS.filter(k => a[k]).length;
    const ceil = 74 + answered * (23 / NQ);
    const top = out[0].s;
    /* the drop flattens out: a big gap reads as a weaker match, never as near-zero */
    out.forEach((o, i) => { o.p = Math.round(Math.max(38, ceil - 45 * (1 - Math.exp(-Math.max(0, top - o.s) / 22)) - i * 1.5)); });
    for (let i = 1; i < out.length; i++) if (out[i].p >= out[i - 1].p) out[i].p = out[i - 1].p - 1;
    return out;
  };
  const recommend = a => scoreAll(a)[0].k;

  /* When the course you asked for came second, say why, from the same numbers. */
  const whyNot = (a, best) => {
    const want = pickOf(a);
    if (!want || want === best) return '';
    const c = C[want], gap = c.cd - CD[a.code || 'none'], lv = c.lv - LV[a.role || 'student'];
    if (gap >= 1) return short(want) + ' assumes more coding than you have today, so ' + short(best) + ' is the stronger first step and ' + short(want) + ' the natural next one.';
    if (lv >= 1.5) return short(want) + ' is pitched at people with more years behind them, so start with ' + short(best) + ' and step up from there.';
    if (lv <= -1.5) return short(want) + ' would repeat much of what you already know; ' + short(best) + ' uses your experience better.';
    return short(want) + ' is a close second for you, so the free demo class is a good place to compare the two.';
  };

  /* The coding line in the result is honest about the gap instead of a fixed phrase. */
  const codeSay = (k, a) => {
    if (!a.code) return '';
    const gap = C[k].cd - CD[a.code];
    if (gap >= 1) return 'it expects more coding than you do today, so plan extra practice in the first weeks';
    return SAY.code[a.code];
  };

  const SAY = {
    role: { student: 'you are starting out, so the course starts from the fundamentals and builds a portfolio as it goes',
      early: 'you have a couple of years behind you, so it moves quickly past the basics',
      mid: 'you have real experience to build on, so it goes straight to production-grade work',
      senior: 'you lead or have 8+ years, so it is pitched at architecture and ownership, not exercises' },
    code: { daily: 'you code every day, so the live builds will feel natural from week one',
      some: 'you know some Python or SQL, which is exactly the starting point it assumes',
      nocode: 'it needs no programming, only the technical mindset you already have',
      none: 'it starts from zero and stays practical, so nothing is assumed' },
    goal: { ai: 'it lands you in AI engineering, the fastest-growing hiring line in India right now',
      fde: 'it is built for the Forward Deployed Engineer role, the AI job companies are racing to fill',
      data: 'it is the shortest path into a data role that companies are hiring for today',
      cloud: 'it pairs architecture with delivery, which is what cloud teams actually want',
      claude: 'it prepares you for the exact Anthropic certification exam and includes the exam prep',
      unsure: 'it keeps the most doors open while still giving you one clear, in-demand credential' },
    when: { now: 'the next batch starts soon, so a counsellor can walk you through enrolment now',
      soon: 'the next batches line up with your 1 to 3 month window',
      explore: 'there is no rush: a free demo class is the best way to test the fit' },
    priority: { salary: 'it is the track with the strongest salary jump for your level',
      cert: 'you finish with a certification employers search for by name',
      projects: 'you build real projects during the course, not after it',
      fee: 'it is the best value at your level, and payment options are available' }
  };

  const QS = [
    { k: 'role', q: 'Where are you in your career right now?', s: 'This sets the level: nothing too basic, nothing that assumes experience you do not have yet.', o: [
      ['student', '🎓', 'Student or fresher', 'Still studying or just graduated'],
      ['early', '🌱', 'Working, 0 to 2 years', 'Early in your first role'],
      ['mid', '💼', 'Working, 3 to 8 years', 'Solid experience to build on'],
      ['senior', '🧭', '8+ years or leading a team', 'You own systems or people']] },
    { k: 'goal', q: 'What do you want to become?', s: 'Pick the closest one. You can change your mind at the end.', o: [
      ['ai', '🤖', 'AI Engineer', 'Agents, RAG and LLM apps'],
      ['fde', '🚀', 'Forward Deployed Engineer', 'Ship AI inside real companies'],
      ['claude', '<img src="' + LOGO.cl[0] + '" alt="" width="26" height="26" style="width:26px;height:26px;object-fit:contain">', 'Claude certified', 'Anthropic exam prep'],
      ['cloud', '☁️', 'Cloud or DevOps', 'AWS and Azure'],
      ['unsure', '🧩', 'Not sure yet', 'Show me my best fit']] },
    /* filled from FOCUS[goal] by renderFocus() each time the goal changes */
    { k: 'focus', q: '', s: '', o: [] },
    { k: 'code', q: 'How comfortable are you with code?', s: 'An honest answer gets a better match. Every path here has a starting point.', o: [
      ['daily', '⌨️', 'I write code most days', 'Python, JavaScript, Java or similar'],
      ['some', '🐍', 'Some Python or SQL', 'Scripts, queries, notebooks'],
      ['nocode', '🛠️', 'Technical, not a coder', 'Tools, admin, configuration'],
      ['none', '🚀', 'Starting from zero', 'No coding yet, and that is fine']] },
    { k: 'when', q: 'When would you like to start?', s: 'This tells us which batch to line you up for.', o: [
      ['now', '⚡', 'This month', 'Ready to go'],
      ['soon', '📅', 'In 1 to 3 months', 'Planning ahead'],
      ['explore', '🔍', 'Just exploring', 'Comparing options for now']] },
    { k: 'priority', q: 'What matters most to you?', s: 'Last question.', o: [
      ['salary', '📈', 'The biggest salary jump', 'Move up a pay band'],
      ['cert', '🏅', 'A certification employers know', 'Microsoft, AWS or Anthropic'],
      ['projects', '🧪', 'Hands-on portfolio projects', 'Work you can show'],
      ['fee', '💸', 'The lowest fee', 'Best value for money']] }
  ];
  const ORDER = ANSWER_KEYS.concat(['lead', 'otp', 'result']);
  /* The options a step shows right now: the focus step depends on the goal. */
  const optsFor = k => k === 'focus' ? ((FOCUS[answers.goal] || {}).o || []) : (QS.find(s => s.k === k) || { o: [] }).o;

  /* ---------------------------------------------------------------- markup */
  const I = {
    spark: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 2.5l2.2 6.3 6.3 2.2-6.3 2.2L11 19.5l-2.2-6.3L2.5 11l6.3-2.2z"/><path d="M19 14.5l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z" opacity=".8"/></svg>',
    x: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    back: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>',
    chk: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
    clock: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    live: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2.5" y="5" width="19" height="13" rx="2"/><path d="M8 21h8"/></svg>',
    cal: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4.5" width="18" height="17" rx="2"/><path d="M16 2.5v4M8 2.5v4M3 10h18"/></svg>',
    tag: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="8" cy="8" r="1.5"/></svg>',
    wa: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7-2.8-1.1-4.6-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.9s.7-2.1 1-2.4c.3-.3.6-.3.8-.3h.6c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.4.6-.4.4c-.1.1-.3.3-.1.6.2.3.7 1.2 1.600 2 1.1.9 2 1.2 2.3 1.4.3.1.5.1.6-.1l.9-1c.2-.3.4-.2.6-.1l1.9.9c.3.1.5.2.5.3.1.2.1.7-.1 1.3z"/></svg>'
  };
  const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
  const logos = list => (list || '').split(',').map(k => LOGO[k] ? '<img src="' + LOGO[k][0] + '" alt="' + LOGO[k][1] + '"' + (k === 'aws' ? ' class="dtf-aws"' : '') + ' loading="lazy" width="22" height="22">' : '').join('');
  const ringHtml = (id, lg) => '<div class="dtf-ring' + (lg ? ' dtf-ring--lg' : '') + '" id="' + id + '"><svg viewBox="0 0 76 76" aria-hidden="true"><circle class="dtf-ring__bg" cx="38" cy="38" r="32"/><circle class="dtf-ring__fg" cx="38" cy="38" r="32"/></svg><b><span>0</span><small>%</small></b></div>';

  const optsHtml = list => list.map((o, i) =>
      '<button type="button" class="dtf-opt" data-v="' + o[0] + '" style="--i:' + i + '" aria-pressed="false">' +
        '<span class="dtf-opt__ico" aria-hidden="true">' + o[1] + '</span>' +
        '<span>' + esc(o[2]) + '<small>' + esc(o[3]) + '</small></span>' +
        '<span class="dtf-opt__key" aria-hidden="true">' + (i + 1) + '</span>' +
        '<span class="dtf-opt__chk" aria-hidden="true">' + I.chk + '</span>' +
      '</button>').join('');
  const stepHtml = s => '<section class="dtf-step" data-step="' + s.k + '" aria-labelledby="dtf-q-' + s.k + '">' +
    '<h3 class="dtf-q" id="dtf-q-' + s.k + '">' + esc(s.q) + '</h3><p class="dtf-sub">' + esc(s.s) + '</p>' +
    '<div class="dtf-opts">' + optsHtml(s.o) + '</div></section>';

  const leadHtml = '<section class="dtf-step" data-step="lead" aria-labelledby="dtf-q-lead">' +
    '<h3 class="dtf-q" id="dtf-q-lead">Your #1 match is locked in 🔒</h3>' +
    '<p class="dtf-sub">One quick verify and it is yours: we unlock your best-fit course right here, with your personalised plan.</p>' +
    '<div class="dtf-teaser">' + ringHtml('dtf-ring-lead') +
      '<div class="dtf-teaser__txt"><small>Your top match</small><b class="dtf-blur" id="dtf-teaser-name" aria-hidden="true">Forward Deployed Engineer</b><span id="dtf-teaser-meta"></span></div></div>' +
    '<div class="dtf-f"><input id="dtf-name" type="text" autocomplete="name" placeholder=" "><label for="dtf-name">Full name</label></div>' +
    '<div class="dtf-f"><input id="dtf-email" type="email" autocomplete="email" inputmode="email" placeholder=" "><label for="dtf-email">Email address</label></div>' +
    '<div class="dtf-ph">' +
      '<div class="dtf-f dtf-f--cc"><select id="dtf-cc" aria-label="Country code"></select><label for="dtf-cc">Code</label></div>' +
      '<div class="dtf-f dtf-f--num"><input id="dtf-phone" type="tel" autocomplete="tel-national" inputmode="tel" placeholder=" "><label for="dtf-phone">WhatsApp number</label></div>' +
    '</div>' +
    '<button type="button" class="dtf-btn dtf-btn--p" id="dtf-send"><span class="dtf-spin"></span><span class="dtf-lbl">Verify &amp; unlock my best match</span>' + I.arrow + '</button>' +
    '<p class="dtf-st" id="dtf-st-lead" role="alert"></p>' +
    '<p class="dtf-fine">' + I.lock + '<span>We email a 6-digit code to confirm it is you, then your match unlocks instantly. No spam, ever.</span></p>' +
  '</section>';

  const otpHtml = '<section class="dtf-step" data-step="otp" aria-labelledby="dtf-q-otp">' +
    '<h3 class="dtf-q" id="dtf-q-otp">Your match is one code away</h3>' +
    '<p class="dtf-sub">We sent a 6-digit code to <b id="dtf-to"></b>. It is valid for 10 minutes. <button type="button" class="dtf-link" id="dtf-edit">Change email</button></p>' +
    '<div class="dtf-otp" id="dtf-otp">' + [0, 1, 2, 3, 4, 5].map(i => '<input type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="' + (i ? 'off' : 'one-time-code') + '" aria-label="Digit ' + (i + 1) + ' of 6">').join('') + '</div>' +
    '<button type="button" class="dtf-btn dtf-btn--p" id="dtf-verify"><span class="dtf-spin"></span><span class="dtf-lbl">Verify &amp; reveal my best match</span>' + I.arrow + '</button>' +
    '<p class="dtf-st" id="dtf-st-otp" role="alert"></p>' +
    '<p class="dtf-re">Did not get it? <button type="button" id="dtf-resend">Resend the code</button></p>' +
  '</section>';

  const resultHtml = '<section class="dtf-step" data-step="result" aria-labelledby="dtf-res-name">' +
    '<div class="dtf-res__hd">' + ringHtml('dtf-ring-res', true) +
      '<div><span class="dtf-badge">✓ Your best fit</span><h3 class="dtf-res__name" id="dtf-res-name"></h3><div class="dtf-res__grp" id="dtf-res-grp"></div></div></div>' +
    '<div class="dtf-facts" id="dtf-facts"></div>' +
    '<ul class="dtf-why" id="dtf-why"></ul>' +
    '<div class="dtf-cta"><a class="dtf-btn dtf-btn--p" id="dtf-demo" href="/pages/book-demo">Book a free demo class' + I.arrow + '</a><a class="dtf-btn dtf-btn--g" id="dtf-course" href="#">See the course</a></div>' +
    '<div class="dtf-wa"><a class="dtf-btn dtf-btn--wa" id="dtf-wa" href="#" target="_blank" rel="noopener">' + I.wa + 'Ask a counsellor on WhatsApp</a></div>' +
    '<div class="dtf-alts" id="dtf-alts"></div>' +
    '<div class="dtf-links"><button type="button" class="dtf-link" id="dtf-retake">Retake the quiz</button><span class="dtf-hint">A counsellor will also reach you with the syllabus and next batch date.</span></div>' +
  '</section>';

  const markup = () =>
    '<button type="button" class="dtf dtf-launch" id="dtf-launch" aria-haspopup="dialog" aria-controls="dtf-ov" aria-label="Find my course: a 60-second course match">' +
      '<span class="dtf-launch__core"><span class="dtf-launch__icon" aria-hidden="true">' + I.spark + '</span>' +
      '<span class="dtf-launch__txt" aria-hidden="true"><b>Find my course</b><small>60-second match</small></span></span></button>' +
    '<div class="dtf dtf-tip" id="dtf-tip" role="button" tabindex="-1" aria-label="Open the course finder"><b>Not sure where to start?</b>Answer 6 quick questions and get matched to the right live course.<button type="button" class="dtf-tip__x" aria-label="Dismiss">&times;</button></div>' +
    '<div class="dtf dtf-nudge" id="dtf-nudge" role="complementary" aria-label="Course finder">' +
      '<button type="button" class="dtf-nudge__x" aria-label="Dismiss">&times;</button>' +
      '<span class="dtf-eye"><i></i>60-second finder</span><h4>Which course fits you best?</h4>' +
      '<p>Six quick questions that adapt to you. We rank all ' + KEYS.length + ' live programs for your background and goal.</p>' +
      '<div class="dtf-mini" aria-hidden="true"><div>Your top match<em>94%</em><i style="--w:94%;--i:0"></i></div><div>Second best<em>86%</em><i style="--w:86%;--i:1"></i></div><div>Third<em>72%</em><i style="--w:72%;--i:2"></i></div></div>' +
      '<button type="button" class="dtf-btn dtf-btn--p" data-lead-quiz="quiz-scroll">Find my course' + I.arrow + '</button></div>' +
    '<div class="dtf dtf-ov" id="dtf-ov" hidden>' +
      '<div class="dtf-md" id="dtf-md" role="dialog" aria-modal="true" aria-labelledby="dtf-title">' +
        '<svg width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false"><defs><linearGradient id="dtf-grad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ffb547"/><stop offset=".45" stop-color="#ff7a3d"/><stop offset="1" stop-color="#e8412f"/></linearGradient></defs></svg>' +
        '<aside class="dtf-live" id="dtf-live">' +
          '<div class="dtf-aur" aria-hidden="true"><i></i><i></i><i></i></div>' +
          '<span class="dtf-grab" aria-hidden="true"></span>' +
          '<div class="dtf-live__hd"><span class="dtf-eye"><i></i>Live match</span><h4 id="dtf-live-h"></h4><p id="dtf-live-p"></p></div>' +
          '<div class="dtf-radar" aria-hidden="true"><b></b><b></b><b></b><b></b></div>' +
          '<div class="dtf-scan" aria-hidden="true"></div>' +
          '<div class="dtf-rank" id="dtf-rank"></div>' +
          '<div class="dtf-chips" id="dtf-chips" aria-hidden="true"></div>' +
          '<div class="dtf-live__ft"><span>' + logos('ms,cl,aws') + '</span>Live classes by Microsoft, Anthropic and AWS certified instructors</div>' +
          '<p class="dtf-sr" aria-live="polite" id="dtf-say"></p>' +
        '</aside>' +
        '<div class="dtf-main" id="dtf-main">' +
          '<div class="dtf-top"><span class="dtf-eye" id="dtf-title">Course Finder</span><span class="dtf-stepno" id="dtf-stepno"></span>' +
            '<button type="button" class="dtf-x" id="dtf-close" aria-label="Close the course finder">' + I.x + '</button></div>' +
          '<div class="dtf-prog" id="dtf-prog" aria-hidden="true">' + '<i></i>'.repeat(NQ + 1) + '</div>' +
          '<div class="dtf-stage" id="dtf-stage">' + QS.map(stepHtml).join('') + leadHtml + otpHtml + resultHtml + '</div>' +
          '<div class="dtf-foot" id="dtf-foot"><button type="button" class="dtf-back" id="dtf-back">' + I.back + 'Back</button>' +
            '<span class="dtf-hint">Press <kbd>1</kbd>–<kbd>5</kbd> to answer</span></div>' +
        '</div>' +
      '</div>' +
    '</div>';

  /* ----------------------------------------------------------------- state */
  const $ = id => D.getElementById(id);
  let ov, md, stage, launch, tip, nudge;
  let answers = {}, step = 'role', placement = 'quiz-fab', opened = false, pick = '', ranking = [], verified = false;
  let lastFocus = null, scrollLock = null, everOpened = false;
  const geo = { ip: '', city: '', region: '', country: '' };
  let geoDone = false;

  const caps = core.lsGet('dtle_caps') || {};
  const cap = (k, days) => { const t = caps[k]; return !(t && Date.now() - t < days * DAY); };
  const stamp = k => { caps[k] = Date.now(); core.lsSet('dtle_caps', caps); };
  const quiet = PAGE === 'cart' || PAGE === 'book-demo' || /\/checkouts?\//.test(location.pathname);
  const started = Date.now();
  const otherModalOpen = () => {
    const bd = $('bd-overlay');
    if (bd && bd.style.display === 'flex') return true;
    if (HTML.classList.contains('dtlp-open')) return true;   // the course / blog lead popup
    return !!D.querySelector('.dta-ov.dta-open');
  };

  /* --------------------------------------------------------------- helpers */
  const status = (id, msg, bad) => { const el = $(id); if (!el) return; el.textContent = msg || ''; el.className = 'dtf-st' + (msg ? (bad ? ' is-bad' : ' is-ok') : ''); };
  const busy = (btn, on) => { btn.classList.toggle('is-busy', !!on); btn.disabled = !!on; };
  const label = (btn, text) => { const l = btn.querySelector('.dtf-lbl'); if (l) l.textContent = text; };
  const say = msg => { const el = $('dtf-say'); if (el) el.textContent = msg; };
  const countTo = (el, to, dur) => {
    if (!el) return;
    const from = parseInt(el.getAttribute('data-v') || '0', 10) || 0;
    el.setAttribute('data-v', String(to));
    const target = el.querySelector('span') || el;
    const suffix = target === el ? '%' : '';
    if (reduce || from === to) { target.textContent = to + suffix; return; }
    const t0 = performance.now(), d = dur || 800;
    const tick = now => {
      const k = Math.min(1, (now - t0) / d), e = 1 - Math.pow(1 - k, 3);
      target.textContent = Math.round(from + (to - from) * e) + suffix;
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const setRing = (id, pct) => {
    const r = $(id); if (!r) return;
    const fg = r.querySelector('.dtf-ring__fg');
    fg.style.strokeDashoffset = '201.1';
    r.getBoundingClientRect();
    requestAnimationFrame(() => { fg.style.strokeDashoffset = String(201.1 * (1 - pct / 100)); });
    r.querySelector('b').setAttribute('data-v', '0');
    countTo(r.querySelector('b'), pct, 1200);
  };
  const ripple = (el, ev) => {
    if (reduce) return;
    const r = el.getBoundingClientRect();
    const dot = D.createElement('span');
    dot.className = 'dtf-ripple';
    dot.style.left = ((ev && ev.clientX ? ev.clientX : r.left + r.width / 2) - r.left) + 'px';
    dot.style.top = ((ev && ev.clientY ? ev.clientY : r.top + r.height / 2) - r.top) + 'px';
    el.appendChild(dot);
    setTimeout(() => dot.remove(), 700);
  };
  const labelFor = (k, v) => { const o = optsFor(k).find(x => x[0] === v); return o ? (o[1].charAt(0) === '<' ? '' : o[1] + ' ') + o[2] : ''; };

  /* ------------------------------------------------------------ live panel */
  const renderRank = (top, unlocked) => {
    const box = $('dtf-rank');
    const before = {};
    box.querySelectorAll('.dtf-rc').forEach(el => { before[el.getAttribute('data-k')] = el.getBoundingClientRect().top; });
    const keep = {};
    top.forEach((o, i) => {
      const c = C[o.k];
      let el = box.querySelector('.dtf-rc[data-k="' + o.k + '"]');
      if (!el) {
        el = D.createElement('div');
        el.className = 'dtf-rc is-new';
        el.setAttribute('data-k', o.k);
        el.innerHTML = '<span class="dtf-rc__n"></span><span class="dtf-rc__t"><span class="dtf-rc__name"></span><span class="dtf-rc__g"></span></span><span class="dtf-rc__p" data-v="0">0%</span><span class="dtf-bar"><i></i></span>';
        setTimeout(() => el.classList.remove('is-new'), 700);
      }
      el.querySelector('.dtf-rc__n').textContent = String(i + 1);
      const nm = el.querySelector('.dtf-rc__name');
      nm.textContent = c.n;
      nm.classList.toggle('is-locked', !unlocked);
      nm.setAttribute('aria-hidden', unlocked ? 'false' : 'true');
      el.querySelector('.dtf-rc__g').textContent = c.g;
      el.classList.toggle('is-top', i === 0);
      keep[o.k] = true;
      box.appendChild(el);
      const bar = el.querySelector('.dtf-bar i');
      requestAnimationFrame(() => bar.style.setProperty('--w', o.p + '%'));
      countTo(el.querySelector('.dtf-rc__p'), o.p);
    });
    box.querySelectorAll('.dtf-rc').forEach(el => { if (!keep[el.getAttribute('data-k')]) el.remove(); });
    if (reduce) return;
    box.querySelectorAll('.dtf-rc').forEach(el => {
      const was = before[el.getAttribute('data-k')];
      if (was === undefined) return;
      const dy = was - el.getBoundingClientRect().top;
      if (!dy) return;
      el.style.transition = 'none';
      el.style.transform = 'translateY(' + dy + 'px)';
      el.getBoundingClientRect();
      el.style.transition = '';
      el.style.transform = '';
    });
  };

  let note = '';
  const updateLive = unlocked => {
    const n = QS.filter(s => answers[s.k]).length;
    ranking = scoreAll(answers);
    $('dtf-live').classList.toggle('has-rank', n > 0);
    $('dtf-live-h').textContent = unlocked ? 'Your ranking is unlocked' : n === 0 ? 'Scanning ' + KEYS.length + ' live programs' : n < NQ ? 'Your matches so far' : 'Your top matches';
    $('dtf-live-p').textContent = unlocked ? 'Best fit first, ranked against all ' + KEYS.length + ' live programs.'
      : n === 0 ? 'Answer six quick questions and watch the programs rank themselves for you.'
      : n < NQ ? (note ? note + ' ' : '') + (NQ - n) + ' more ' + (NQ - n === 1 ? 'answer' : 'answers') + ' to lock in your best fit.'
      : 'Names unlock with your result.';
    if (n) renderRank(ranking.slice(0, 3), unlocked); else $('dtf-rank').innerHTML = '';
    const chips = QS.filter(s => answers[s.k]);
    $('dtf-chips').innerHTML = chips.map((s, i) => '<span' + (i < chips.length - 1 ? ' style="animation-delay:-1s"' : '') + '>' + esc(labelFor(s.k, answers[s.k])) + '</span>').join('');
    if (n && !unlocked) say(n + ' of ' + NQ + ' answered. Top match strength ' + ranking[0].p + ' percent.');
  };

  /* ----------------------------------------------------------------- steps */
  const show = (name, back) => {
    step = name;
    const idx = ORDER.indexOf(name);
    stage.querySelectorAll('.dtf-step').forEach(p => {
      const on = p.getAttribute('data-step') === name;
      p.classList.toggle('is-on', on);
      p.classList.toggle('is-back', on && !!back);
    });
    const segs = $('dtf-prog').children;
    for (let i = 0; i < segs.length; i++) {
      segs[i].classList.toggle('is-done', i < idx);
      segs[i].classList.toggle('is-cur', i === idx);
    }
    $('dtf-stepno').textContent = idx < NQ ? 'Question ' + (idx + 1) + ' of ' + NQ : name === 'lead' ? 'Almost there' : name === 'otp' ? 'One last step' : 'Your result';
    $('dtf-prog').style.visibility = name === 'result' ? 'hidden' : '';
    $('dtf-back').hidden = idx === 0;
    $('dtf-foot').style.display = name === 'result' ? 'none' : '';
    stage.scrollTop = 0;
    if (!core.isMobile) {
      setTimeout(() => {
        const f = stage.querySelector('.dtf-step.is-on .dtf-opt.is-sel') || stage.querySelector('.dtf-step.is-on .dtf-opt, .dtf-step.is-on input');
        if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
      }, reduce ? 0 : 160);
    }
    core.track('dtle_step', { step: name, placement: placement });
  };

  const choose = (opt, ev) => {
    const panel = opt.closest('.dtf-step');
    if (!panel || panel.getAttribute('data-busy')) return;
    panel.setAttribute('data-busy', '1');
    const key = panel.getAttribute('data-step');
    panel.querySelectorAll('.dtf-opt').forEach(b => {
      const on = b === opt;
      b.classList.toggle('is-sel', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    ripple(opt, ev);
    const prev = answers[key];
    answers[key] = opt.getAttribute('data-v');
    if (key === 'goal' && prev !== answers.goal) { delete answers.focus; renderFocus(); }
    const picked = optsFor(key).find(x => x[0] === answers[key]);
    note = (picked && picked[6]) || '';
    core.track('dtle_answer', { question: key, answer: answers[key], placement: placement });
    updateLive(false);
    const next = ORDER[ORDER.indexOf(key) + 1];
    setTimeout(() => {
      panel.removeAttribute('data-busy');
      if (next !== 'lead') { show(next); return; }
      pick = recommend(answers);
      if (verified) { finish(); return; }
      prepareLead();
      show('lead');
    }, reduce ? 80 : 480);
  };

  const renderFocus = () => {
    const f = FOCUS[answers.goal] || FOCUS.unsure;
    const panel = stage.querySelector('.dtf-step[data-step="focus"]');
    panel.querySelector('.dtf-q').textContent = f.q;
    panel.querySelector('.dtf-sub').textContent = f.s;
    panel.querySelector('.dtf-opts').innerHTML = optsHtml(f.o);
  };

  const prepareLead = () => {
    const c = C[pick];
    $('dtf-teaser-name').textContent = c.n;
    $('dtf-teaser-meta').textContent = c.g + ' · plus 2 more strong options';
    setRing('dtf-ring-lead', ranking[0] ? ranking[0].p : 90);
  };

  const quizString = () => ANSWER_KEYS.map(k => k + '=' + (answers[k] || '')).join(';');

  /* ------------------------------------------------------------- open/close */
  /* Inline !important on html and body: edge-stack-section ships
     html, body { overflow: auto !important }, which beats a plain inline style. */
  const lockScroll = on => {
    const b = D.body;
    if (on) {
      const gap = window.innerWidth - HTML.clientWidth;
      scrollLock = {
        b: [b.style.getPropertyValue('overflow'), b.style.getPropertyPriority('overflow')],
        h: [HTML.style.getPropertyValue('overflow'), HTML.style.getPropertyPriority('overflow')],
        p: b.style.paddingRight
      };
      b.style.setProperty('overflow', 'hidden', 'important');
      HTML.style.setProperty('overflow', 'hidden', 'important');
      if (gap > 0) b.style.paddingRight = gap + 'px';
    } else if (scrollLock) {
      b.style.setProperty('overflow', scrollLock.b[0], scrollLock.b[1]);
      HTML.style.setProperty('overflow', scrollLock.h[0], scrollLock.h[1]);
      b.style.paddingRight = scrollLock.p;
      scrollLock = null;
    }
  };

  const fetchGeo = () => {
    if (geoDone) return;
    geoDone = true;
    try {
      fetch('https://ipwho.is/').then(r => r.json()).then(d => {
        if (d && d.success !== false) { geo.ip = d.ip || ''; geo.city = d.city || ''; geo.region = d.region || ''; geo.country = d.country || ''; }
      }).catch(() => {});
    } catch (e) {}
  };

  const open = pl => {
    if (opened || !ov || otherModalOpen()) return;
    opened = true;
    everOpened = true;
    placement = pl || 'quiz-link';
    lastFocus = D.activeElement;
    answers = {};
    pick = '';
    stage.querySelectorAll('.dtf-opt').forEach(b => { b.classList.remove('is-sel'); b.setAttribute('aria-pressed', 'false'); });
    status('dtf-st-lead', '');
    status('dtf-st-otp', '');
    updateLive(false);
    lockScroll(true);
    HTML.classList.add('dtf-modal');   // tucks the Tidio bubble away so it never sits on the quiz buttons
    ov.hidden = false;
    ov.getBoundingClientRect();
    ov.classList.add('is-open');
    hideTip();
    hideNudge();
    launch.classList.remove('is-on');
    show('role');
    fetchGeo();
    core.track('dtle_open', { placement: placement, page: PAGE });
  };

  const close = () => {
    if (!opened) return;
    opened = false;
    ov.classList.remove('is-open');
    setTimeout(() => { if (!opened) { ov.hidden = true; HTML.classList.remove('dtf-modal'); } }, reduce ? 0 : 400);
    lockScroll(false);
    if (step !== 'result') core.track('dtle_close', { step: step, placement: placement });
    showLauncher();
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  };

  /* ---------------------------------------------------------- lead + OTP */
  let jsonpN = 0;
  const call = p => new Promise((resolve, reject) => {
    if (!core.GAS) { reject(new Error('The verification service is not available on this page.')); return; }
    const cb = 'dtf_cb_' + (++jsonpN) + '_' + Date.now();
    const s = D.createElement('script');
    const cleanup = () => { clearTimeout(timer); try { delete window[cb]; } catch (e) { window[cb] = undefined; } if (s.parentNode) s.parentNode.removeChild(s); };
    const timer = setTimeout(() => { cleanup(); reject(new Error('Request timed out. Please try again.')); }, 45000);
    window[cb] = data => { cleanup(); resolve(data || {}); };
    s.onerror = function () { var u = s.src; var fail = function () { cleanup(); reject(new Error('Could not reach the verification service. Please try again.')); }; if (!window.fetch) { fail(); return; } fetch(u, { mode: 'cors', redirect: 'follow', cache: 'no-store' }).then(function (r) { return r.text(); }).then(function (t) { var m = t.match(/^\s*[\w$]+\(([\s\S]*)\)\s*;?\s*$/); var d = JSON.parse(m ? m[1] : t); if (window[cb]) window[cb](d); else fail(); }).catch(fail); };   /* 6 Oct 2026: fetch() retry before failing */
    p.callback = cb;
    s.src = core.GAS + '?' + core.qs(p);
    D.head.appendChild(s);
  });

  const emailVal = () => $('dtf-email').value.trim().toLowerCase();
  const validEmail = v => { const at = v.indexOf('@'), dot = v.lastIndexOf('.'); return at > 0 && dot > at + 1 && dot < v.length - 2 && v.indexOf(' ') < 0; };
  const fieldErr = (id, msg) => {
    const f = $(id).closest('.dtf-f');
    f.classList.remove('is-err');
    f.getBoundingClientRect();
    f.classList.add('is-err');
    status('dtf-st-lead', msg, true);
    $(id).focus();
  };

  let resendT = null;
  const resendCountdown = () => {
    const btn = $('dtf-resend');
    let left = 30;
    clearInterval(resendT);
    btn.disabled = true;
    btn.textContent = 'Resend in ' + left + 's';
    resendT = setInterval(() => {
      left -= 1;
      if (left <= 0) { clearInterval(resendT); btn.disabled = false; btn.textContent = 'Resend the code'; return; }
      btn.textContent = 'Resend in ' + left + 's';
    }, 1000);
  };

  const sendCode = btn => {
    busy(btn, true);
    /* 6 Oct 2026 (theme 80.0): details travel with the code request so the lead is saved even without the code;
     * a timeout or a blocked load counts as sent (the email nearly always arrives). */
    return call({ action: 'sendOtp', email: emailVal(), tag: TAG, name: $('dtf-name').value.trim(),
      phone: $('dtf-phone').value.replace(/[^0-9]/g, ''), countryCode: $('dtf-cc').value, page: location.href.split('#')[0] })
      .catch(err => { if (/timed out|reach the verification/i.test(err && err.message)) return { status: 'sent', slow: true }; throw err; }).then(r => {
      busy(btn, false);
      if (r.status === 'sent') return true;
      status(step === 'otp' ? 'dtf-st-otp' : 'dtf-st-lead', r.message || 'Could not send the code. Please try again.', true);
      return false;
    }).catch(err => {
      busy(btn, false);
      status(step === 'otp' ? 'dtf-st-otp' : 'dtf-st-lead', err.message || 'Could not reach the verification service.', true);
      return false;
    });
  };

  const otpBoxes = () => Array.prototype.slice.call($('dtf-otp').querySelectorAll('input'));
  const otpValue = () => otpBoxes().map(i => i.value).join('');
  const clearOtp = () => otpBoxes().forEach(b => { b.value = ''; b.classList.remove('is-filled'); });
  const fillOtp = (digits, from) => {
    const boxes = otpBoxes();
    let j = from;
    digits.forEach(d => { if (j > 5) return; boxes[j].value = d; boxes[j].classList.add('is-filled'); j += 1; });
    const next = boxes[Math.min(j, 5)];
    if (next) next.focus();
    if (otpValue().length === 6) verify();
  };

  let verifying = false;
  const verify = () => {
    if (verifying) return;
    status('dtf-st-otp', '');
    const code = otpValue();
    const wrap = $('dtf-otp');
    if (code.length !== 6) {
      wrap.classList.remove('is-err'); wrap.getBoundingClientRect(); wrap.classList.add('is-err');
      status('dtf-st-otp', 'Enter the 6-digit code from your email.', true);
      return;
    }
    verifying = true;
    const btn = $('dtf-verify');
    busy(btn, true);
    const c = C[pick];
    const p = core.params(placement);
    p.action = 'verifyOtp'; p.email = emailVal(); p.tag = TAG; p.code = code; p.dtle = '1';
    p.name = $('dtf-name').value.trim(); p.phone = $('dtf-phone').value.replace(/[^0-9]/g, ''); p.countryCode = $('dtf-cc').value;
    p.course = c.n; p.courseCode = pick; p.courseHandle = c.h; p.quiz = quizString();
    p.ip = geo.ip; p.city = geo.city; p.region = geo.region; p.country = geo.country;
    call(p).then(r => {
      verifying = false;
      busy(btn, false);
      if (r.status === 'verified') {
        verified = true;
        core.onLead(TAG, placement, { course: pick });
        stamp('done');
        finish();
        return;
      }
      wrap.classList.remove('is-err'); wrap.getBoundingClientRect(); wrap.classList.add('is-err');
      status('dtf-st-otp', r.message || 'That code did not match. Please check it and try again.', true);
      clearOtp();
      otpBoxes()[0].focus();
    }).catch(err => {
      verifying = false;
      busy(btn, false);
      status('dtf-st-otp', err.message || 'Could not reach the verification service.', true);
    });
  };

  /* ---------------------------------------------------------------- result */
  const confetti = () => {
    if (reduce) return;
    const main = $('dtf-main');
    const ring = $('dtf-ring-res');
    const mr = main.getBoundingClientRect(), rr = ring.getBoundingClientRect();
    const box = D.createElement('div');
    box.className = 'dtf-confetti';
    const cols = ['#e8412f', '#ff7a3d', '#ffb547', '#1e7f4f', '#6f4bff', '#ff4f8b'];
    let s = '';
    for (let i = 0; i < 44; i++) {
      const a = Math.random() * Math.PI * 2, d = 110 + Math.random() * 250;
      s += '<i style="left:' + Math.round(rr.left - mr.left + rr.width / 2) + 'px;top:' + Math.round(rr.top - mr.top + rr.height / 2) + 'px;' +
        '--x:' + Math.round(Math.cos(a) * d) + 'px;--y:' + Math.round(Math.sin(a) * d * 0.8 - 60) + 'px;--r:' + Math.round(Math.random() * 720 - 360) + 'deg;' +
        'background:' + cols[i % cols.length] + ';animation-delay:' + Math.round(Math.random() * 140) + 'ms;' +
        (i % 3 === 0 ? 'border-radius:50%;width:8px;height:8px' : '') + '"></i>';
    }
    box.innerHTML = s;
    main.appendChild(box);
    setTimeout(() => box.remove(), 1900);
  };

  const renderResult = () => {
    const c = C[pick];
    const pct = ranking[0] ? ranking[0].p : 92;
    $('dtf-res-name').textContent = c.n;
    $('dtf-res-grp').innerHTML = logos(c.l) + '<span>' + esc(c.g) + '</span>';
    const facts = [[I.clock, c.wk + ' weeks'], [I.live, c.hrs + ' hrs live'], [I.cal, 'Sat–Sun, 8–11 PM IST']];
    const price = priceOf(c.h);
    if (price) facts.push([I.tag, price]);
    $('dtf-facts').innerHTML = facts.map((f, i) => '<span style="--i:' + i + '">' + f[0] + esc(f[1]) + '</span>').join('');
    /* The reasons follow the visitor's own answers: the course line, the
       follow-up answer, then level, coding (honest about any gap), priority and
       timing, and a "why not" line when the course they asked for came second. */
    const cap1 = t => t.charAt(0).toUpperCase() + t.slice(1) + '.';
    const fo = focusOpt(answers);
    const why = [c.w];
    if (fo && fo[5] && (!fo[7] || fo[7] === pick)) why.push(cap1(fo[5]));
    [SAY.role[answers.role], codeSay(pick, answers), SAY.priority[answers.priority], SAY.goal[answers.goal], SAY.when[answers.when]]
      .forEach(t => { if (t && why.length < 4) why.push(cap1(t)); });
    const wn = whyNot(answers, pick);
    if (wn) why.splice(1, 0, wn);
    $('dtf-why').innerHTML = why.slice(0, wn ? 5 : 4).map((t, i) => '<li style="--i:' + i + '">' + esc(t) + '</li>').join('');
    $('dtf-course').href = c.u;
    $('dtf-wa').href = 'https://wa.me/' + WA + '?text=' + encodeURIComponent('Hi, the 360 Digital Transformation course finder matched me with ' + c.n + '. Can you tell me about the next batch?');
    const alts = ranking.filter(o => o.k !== pick).slice(0, 2);
    $('dtf-alts').innerHTML = alts.length ? '<h5>Also a strong match</h5>' + alts.map(o =>
      '<a class="dtf-alt" href="' + C[o.k].u + '"><b>' + esc(C[o.k].n) + '</b><em>' + o.p + '%</em><span class="dtf-bar"><i style="--w:' + o.p + '%"></i></span></a>').join('') : '';
    return pct;
  };

  const finish = () => {
    const pct = renderResult();
    show('result');
    updateLive(true);
    setRing('dtf-ring-res', pct);
    setTimeout(confetti, reduce ? 0 : 260);
    say('Your best fit is ' + C[pick].n + ', a ' + pct + ' percent match.');
    core.track('dtle_result', { course: pick, match: pct, placement: placement });
  };

  /* --------------------------------------------- placement on the screen */
  let cands = [], candAt = 0;
  const scan = () => {
    const t = Date.now();
    if (t - candAt < 1200 || !D.body) return;
    candAt = t;
    const out = [], all = D.body.getElementsByTagName('*');
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      const cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      if (el.closest('.dtf, #tidio-chat, #dtle, [id^="PBar"]')) continue;
      out.push(el);
    }
    cands = out;
  };
  const obstacleTop = (left, right, top, bottom, vh) => {
    let best = 0;
    for (let i = 0; i < cands.length; i++) {
      const el = cands[i];
      if (el.isConnected === false) continue;
      const r = el.getBoundingClientRect();
      if (r.width < 40 || r.height < 16 || r.height > vh * 0.5 || r.top < vh * 0.35) continue;
      if (r.right <= left || r.left >= right || r.bottom <= top || r.top >= bottom) continue;
      const cs = window.getComputedStyle(el);
      if (cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) < 0.05) continue;
      if (!best || r.top < best) best = r.top;
    }
    return best;
  };
  const tidio = () => {
    const host = $('tidio-chat');
    const sr = host && host.shadowRoot;
    if (!sr) return null;
    const chat = sr.querySelector('.chat');
    if (chat) { const r = chat.getBoundingClientRect(); if (r.width > 150 && r.height > 150) return { open: true }; }
    const b = sr.querySelector('#button-body');
    if (!b) return null;
    const r = b.getBoundingClientRect();
    if (r.width < 10 || r.height < 10 || r.top >= window.innerHeight) return null;
    return { open: false, r: r };
  };
  let lastR = -1, lastB = -1;
  const place = () => {
    if (!launch) return;
    const W = HTML.clientWidth, H = window.innerHeight, small = W <= 640;
    const t = tidio();
    const chatting = !!(t && t.open);
    [launch, tip, nudge].forEach(el => el.classList.toggle('is-chat', chatting));
    launch.classList.toggle('is-mini', small || launch.classList.contains('is-scroll-mini'));
    if (chatting) return;
    let right = small ? 14 : 24, bottom = small ? 14 : 24;
    if (t && t.r) {
      right = Math.max(8, Math.round(W - t.r.right));
      bottom = Math.round(H - t.r.top) + (small ? 10 : 14);
    }
    const lw = launch.offsetWidth || 56, lh = launch.offsetHeight || 56;
    for (let pass = 0; pass < 5; pass++) {
      const hit = obstacleTop(W - right - lw, W - right, H - bottom - lh, H - bottom, H);
      if (!hit) break;
      const next = Math.round(H - hit) + 10;
      if (next <= bottom) break;
      bottom = next;
    }
    bottom = Math.min(bottom, Math.round(H * 0.5));
    if (right !== lastR) { HTML.style.setProperty('--dtf-r', right + 'px'); lastR = right; }
    if (bottom !== lastB) { HTML.style.setProperty('--dtf-b', bottom + 'px'); lastB = bottom; }
  };
  let placeQueued = false;
  const queuePlace = () => {
    if (placeQueued) return;
    placeQueued = true;
    requestAnimationFrame(() => { placeQueued = false; place(); });
  };

  const showLauncher = () => {
    if (quiet || opened || !cap('done', 30)) return;
    scan();
    place();
    launch.classList.add('is-on');
  };
  const hideTip = () => { if (tip) tip.classList.remove('is-on'); };
  const hideNudge = () => { if (nudge) nudge.classList.remove('is-on'); };

  /* ------------------------------------------------------------------ init */
  const init = () => {
    const wrap = D.createElement('div');
    wrap.innerHTML = markup();
    while (wrap.firstChild) D.body.appendChild(wrap.firstChild);
    ov = $('dtf-ov'); md = $('dtf-md'); stage = $('dtf-stage'); launch = $('dtf-launch'); tip = $('dtf-tip'); nudge = $('dtf-nudge');

    const cc = $('dtf-cc');
    if (typeof window.dtBuildCountryCodeSelect === 'function') window.dtBuildCountryCodeSelect(cc);
    if (!cc.options.length) ['+91', '+1', '+44', '+61', '+971', '+65', '+966', '+974', '+27', '+234', '+254', '+60', '+49', '+33'].forEach(c => { const o = D.createElement('option'); o.value = c; o.textContent = c; cc.appendChild(o); });

    /* dialog interactions */
    ov.addEventListener('click', e => {
      if (e.target === ov) { close(); return; }
      const opt = e.target.closest('.dtf-opt');
      if (opt) choose(opt, e);
    });
    $('dtf-close').addEventListener('click', close);
    $('dtf-back').addEventListener('click', () => {
      const i = ORDER.indexOf(step);
      if (i > 0) show(ORDER[i - 1], true);
    });
    $('dtf-edit').addEventListener('click', () => { show('lead', true); $('dtf-email').focus(); });
    $('dtf-retake').addEventListener('click', () => {
      answers = {};
      stage.querySelectorAll('.dtf-opt').forEach(b => { b.classList.remove('is-sel'); b.setAttribute('aria-pressed', 'false'); });
      updateLive(false);
      show('role', true);
    });
    D.addEventListener('keydown', e => {
      if (!opened) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') {
        const f = Array.prototype.filter.call(md.querySelectorAll('button, a[href], input, select'), el => !el.disabled && el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && D.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && D.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!md.contains(D.activeElement)) { e.preventDefault(); first.focus(); }
        return;
      }
      const tag = (D.activeElement && D.activeElement.tagName) || '';
      if (ORDER.indexOf(step) < NQ && /^[1-9]$/.test(e.key) && !/INPUT|SELECT|TEXTAREA/.test(tag)) {
        const o = stage.querySelectorAll('.dtf-step.is-on .dtf-opt')[parseInt(e.key, 10) - 1];
        if (o) { e.preventDefault(); choose(o); }
      }
    });

    /* drag the dark header down to close the bottom sheet */
    const live = $('dtf-live');
    let dragY = null;
    live.addEventListener('pointerdown', e => {
      if (HTML.clientWidth > 820) return;
      dragY = e.clientY;
      md.classList.add('is-drag');
      try { live.setPointerCapture(e.pointerId); } catch (err) {}
    });
    live.addEventListener('pointermove', e => {
      if (dragY === null) return;
      md.style.transform = 'translateY(' + Math.max(0, e.clientY - dragY) + 'px)';
    });
    const endDrag = e => {
      if (dragY === null) return;
      const dy = e.clientY - dragY;
      dragY = null;
      md.classList.remove('is-drag');
      md.style.transform = '';
      if (dy > 110) close();
    };
    live.addEventListener('pointerup', endDrag);
    live.addEventListener('pointercancel', endDrag);

    /* lead form */
    const sendBtn = $('dtf-send');
    sendBtn.addEventListener('click', () => {
      status('dtf-st-lead', '');
      if (!$('dtf-name').value.trim()) { fieldErr('dtf-name', 'Please enter your name.'); return; }
      if (!validEmail(emailVal())) { fieldErr('dtf-email', 'Please enter a valid email address.'); return; }
      const ph = $('dtf-phone').value.replace(/[^0-9]/g, '');
      if (ph.length < 7 || ph.length > 15) { fieldErr('dtf-phone', 'Please enter a valid phone number.'); return; }
      label(sendBtn, 'Sending your code');
      sendCode(sendBtn).then(ok => {
        label(sendBtn, 'Verify & unlock my best match');
        if (!ok) return;
        $('dtf-to').textContent = emailVal();
        clearOtp();
        show('otp');
        resendCountdown();
        if (core.isMobile) setTimeout(() => otpBoxes()[0].focus(), 300);
      });
    });
    ['dtf-name', 'dtf-email', 'dtf-phone'].forEach(id => {
      $(id).addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); sendBtn.click(); } });
      $(id).addEventListener('input', () => { const f = $(id).closest('.dtf-f'); f.classList.remove('is-err'); });
    });

    /* one-time code */
    const otp = $('dtf-otp');
    otp.addEventListener('input', e => {
      const box = e.target, i = otpBoxes().indexOf(box);
      const digits = box.value.replace(/[^0-9]/g, '').split('');
      box.value = '';
      box.classList.remove('is-filled');
      otp.classList.remove('is-err');
      if (digits.length) fillOtp(digits, i);
    });
    otp.addEventListener('keydown', e => {
      const boxes = otpBoxes(), i = boxes.indexOf(e.target);
      if (e.key === 'Backspace' && !e.target.value && i > 0) { e.preventDefault(); boxes[i - 1].value = ''; boxes[i - 1].classList.remove('is-filled'); boxes[i - 1].focus(); }
      else if (e.key === 'ArrowLeft' && i > 0) boxes[i - 1].focus();
      else if (e.key === 'ArrowRight' && i < 5) boxes[i + 1].focus();
      else if (e.key === 'Enter') verify();
    });
    otp.addEventListener('paste', e => {
      const txt = ((e.clipboardData || window.clipboardData) && (e.clipboardData || window.clipboardData).getData('text')) || '';
      const d = txt.replace(/[^0-9]/g, '').slice(0, 6);
      if (!d) return;
      e.preventDefault();
      clearOtp();
      fillOtp(d.split(''), 0);
    });
    $('dtf-verify').addEventListener('click', verify);
    $('dtf-resend').addEventListener('click', () => {
      status('dtf-st-otp', '');
      sendCode($('dtf-verify')).then(ok => { if (ok) { status('dtf-st-otp', 'A new code is on its way.', false); resendCountdown(); } });
    });

    /* result: hand over to the existing demo popup, course preselected */
    $('dtf-demo').addEventListener('click', e => {
      const trigger = D.querySelector('[data-book-demo], .dt-cta, .dt-feat__btn');
      const bd = $('bd-overlay');
      if (!trigger || !bd) return;
      e.preventDefault();
      const vals = { name: $('dtf-name').value.trim(), email: emailVal(), phone: $('dtf-phone').value.replace(/[^0-9]/g, ''), cc: $('dtf-cc').value };
      close();
      trigger.click();
      setTimeout(() => {
        const set = (id, v) => { const el = $(id); if (el && v) el.value = v; };
        set('bd-name', vals.name); set('bd-email', vals.email); set('bd-phone', vals.phone); set('bd-country-code', vals.cc);
        const o = D.querySelector('#bd-course-list [data-code="' + pick + '"]');
        if (o) o.click();
        core.track('dtle_demo_handoff', { course: pick, placement: placement });
      }, 80);
    });

    /* launcher, tooltip, nudge */
    launch.addEventListener('click', () => open('quiz-fab'));
    tip.addEventListener('click', e => {
      if (e.target.closest('.dtf-tip__x')) { hideTip(); stamp('tip'); return; }
      hideTip();
      open('quiz-tip');
    });
    nudge.querySelector('.dtf-nudge__x').addEventListener('click', () => { hideNudge(); stamp('scroll'); });

    window.addEventListener('resize', () => { candAt = 0; scan(); queuePlace(); });
    let lastY = window.scrollY || 0, idleT = null;
    window.addEventListener('scroll', () => {
      const y = window.scrollY || 0, dy = y - lastY;
      lastY = y;
      if (Math.abs(dy) > 6 && y > 160) {
        launch.classList.add('is-scroll-mini');
        hideTip();
      }
      clearTimeout(idleT);
      idleT = setTimeout(() => { launch.classList.remove('is-scroll-mini'); queuePlace(); }, 1400);
      queuePlace();
    }, { passive: true });
    D.addEventListener('transitionend', e => {
      const el = e.target;
      if (!el || el.nodeType !== 1 || el.closest('.dtf')) return;
      const r = el.getBoundingClientRect();
      if (r.width > 40 && r.top > window.innerHeight * 0.35 && r.top < window.innerHeight) { candAt = 0; scan(); queuePlace(); }
    }, true);
    [400, 1500, 4000, 9000].forEach(ms => setTimeout(() => { candAt = 0; scan(); place(); }, ms));
    setInterval(() => { if (!document.hidden) queuePlace(); }, 2500);
    setTimeout(showLauncher, reduce ? 0 : 1600);

    if (!quiet && cap('tip', 3) && cap('done', 30) && !core.isLead()) {
      setTimeout(() => {
        if (opened || !launch.classList.contains('is-on') || otherModalOpen()) return;
        stamp('tip');
        tip.classList.add('is-on');
        core.track('dtle_tip', { page: PAGE });
        setTimeout(hideTip, 9000);
      }, 14000);
    }

    D.addEventListener('dtle:lead', () => { launch.classList.remove('is-on'); hideTip(); hideNudge(); });

    /* explicit triggers: [data-lead-quiz], links ending #find-my-course, or landing on that hash */
    D.addEventListener('click', e => {
      const t = e.target.closest ? e.target.closest('[data-lead-quiz], a[href$="#find-my-course"]') : null;
      if (!t || t.closest('#dtf-ov')) return;
      e.preventDefault();
      if (t.closest('#dtf-nudge')) { hideNudge(); stamp('scroll'); }
      open(t.getAttribute('data-lead-quiz') || 'quiz-link');
    });
    if (/#find-my-course$/.test(location.hash)) setTimeout(() => open('quiz-link'), 400);

    /* auto-open, 1 Oct 2026: after AUTO_MS of time the page is actually on
       screen (a background tab does not count), at most once every 3 days,
       never for a known lead or anyone who finished the finder in the last 30
       days, and never on top of another dialog or while the visitor is typing
       in a page form. The course / blog lead popup (dt-lead-popup.js) opens at
       10 s on those pages; if it was shown this visit the finder stays a
       button, so nobody gets two popups. ?dtf=1 forces it, for testing. */
    const AUTO_MS = 20000;
    const forceAuto = /[?&]dtf=1\b/.test(location.search);
    if (forceAuto || (!quiet && cap('auto', 3) && cap('done', 30) && !core.isLead())) {
      let seen = 0, last = Date.now();
      const popupShown = () => { const v = core.lsGet('dtlp_visit'); return !!(v && v.id && core.lsGet('dtlp_seen') === v.id); };
      const typing = () => { const a = D.activeElement; return !!(a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !a.closest('.dtf')); };
      const autoT = setInterval(() => {
        const now = Date.now();
        if (D.visibilityState === 'visible') seen += now - last;
        last = now;
        if (everOpened) { clearInterval(autoT); return; }      // they found it themselves
        if (seen < AUTO_MS) return;
        if (!forceAuto && (core.isLead() || popupShown())) { clearInterval(autoT); return; }
        if (otherModalOpen() || typing()) return;              // wait, try again next tick
        clearInterval(autoT);
        stamp('auto');
        open('quiz-auto');
      }, 500);
    }

    /* exit intent: desktop, after 10s, once a week, never for a known lead */
    D.addEventListener('mouseout', e => {
      if (core.isMobile || quiet || opened || core.isLead() || !cap('exit', 7)) return;
      if (e.relatedTarget || e.clientY > 0 || Date.now() - started < 10000 || otherModalOpen()) return;
      stamp('exit');
      open('quiz-exit');
    });

    /* scroll nudge: 55% down and 40s in, once a week */
    if (/^(guide|course|home|collection|article)$/.test(PAGE) && !quiet && cap('scroll', 7) && !core.isLead()) {
      let shown = false;
      const onScroll = () => {
        if (shown) return;
        const h = HTML;
        const depth = ((window.scrollY || 0) + window.innerHeight) / Math.max(1, h.scrollHeight);
        if (depth < 0.55 || Date.now() - started < 40000 || opened || otherModalOpen()) return;
        shown = true;
        stamp('scroll');
        hideTip();
        place();
        nudge.classList.add('is-on');
        core.track('dtle_nudge', { placement: 'quiz-scroll', page: PAGE });
        window.removeEventListener('scroll', onScroll);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(onScroll, 40500);
    }

    /* inline card inside the certification guides */
    if (PAGE === 'guide') {
      const kit = $('dtg');
      if (kit && !kit.querySelector('.dtf-inline')) {
        const card = D.createElement('aside');
        card.className = 'dtf dtf-inline';
        card.innerHTML = '<div><span class="dtf-eye"><i></i>60-second course finder</span>' +
          '<h3>Is this the right certification for you?</h3>' +
          '<p>Six questions that adapt to where you are and where you want to go. We rank all ' + KEYS.length + ' live programs and show you the one that fits.</p></div>' +
          '<button type="button" class="dtf-btn dtf-btn--p" data-lead-quiz="quiz-guide">Find my course' + I.arrow + '</button>';
        const h2s = kit.querySelectorAll('h2');
        const anchor = h2s.length >= 4 ? h2s[3] : null;
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(card, anchor); else kit.appendChild(card);
      }
    }

    window.dtLE = { open: open, close: close, recommend: recommend, score: scoreAll, scoreOf: scoreOf, focus: FOCUS, whyNot: whyNot, courses: C, version: '3.0' };
  };

  /* Wait for the stylesheet (it loads without blocking render) so the
     launcher never flashes unstyled at the bottom of the page. */
  const whenStyled = cb => {
    const link = $('dtf-css');
    if (!link || link.media === 'all') { cb(); return; }
    let done = false;
    const go = () => { if (!done) { done = true; cb(); } };
    link.addEventListener('load', () => setTimeout(go, 0));
    const t = setInterval(() => { if (link.media === 'all') { clearInterval(t); go(); } }, 150);
    setTimeout(() => { clearInterval(t); go(); }, 8000);
  };

  const start = () => whenStyled(init);
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', start); else start();
})();